import { jwtDecode } from 'jwt-decode';
import api from './api';

const AUTH_TOKEN_NAME = process.env.REACT_APP_AUTH_TOKEN_NAME || 'authKey';
const CSRF_TOKEN_NAME = process.env.REACT_APP_CSRF_TOKEN_NAME || 'securityKey';

// Check token existence and expiration
const checkToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_NAME);
  if (!token) {
    return { tokenInLocalStorage: false };
  }

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const isExpired = decodedToken.exp ? decodedToken.exp < currentTime : false;
    return {
      tokenInLocalStorage: true,
      token,
      isExpired,
      decoded: decodedToken,
    };
  } catch (error) {
    // Treat decoding errors as an invalid/expired token
    return { tokenInLocalStorage: true, isExpired: true };
  }
};

const authService = {
  // Login user
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });

    if (response.data && response.data.token) {
      localStorage.setItem(AUTH_TOKEN_NAME, response.data.token);
    }

    return response.data;
  },

  // Get current user info
  getCurrentUserProfile: async () => {
    const tokenInfo = checkToken();
    if (!tokenInfo.tokenInLocalStorage || tokenInfo.isExpired) {
      return null;
    }

    const response = await api.get('/auth/me');
    return response.data.data || response.data;
  },

  // User management functions
  getAllUsers: async () => {
    const response = await api.get('/auth/users');
    return response.data.data || response.data;
  },

  getUserById: async (id) => {
    if (!id) throw new Error('User ID is required');
    const response = await api.get(`/auth/users/${id}`);
    return response.data.data || response.data;
  },

  registerUser: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  updateUser: async (id, userData) => {
    if (!id) throw new Error('User ID is required');
    const response = await api.put(`/auth/users/${id}`, userData);
    return response.data;
  },

  deleteUser: async (id) => {
    if (!id) throw new Error('User ID is required');
    const response = await api.delete(`/auth/users/${id}`);
    return response.data || { success: true, message: 'User deleted' };
  },

  resetPassword: async (id, newPassword) => {
    if (!id || !newPassword) throw new Error('User ID and new password are required');
    const response = await api.put(`/auth/users/${id}/reset-password`, { password: newPassword });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem(AUTH_TOKEN_NAME);
    localStorage.removeItem(CSRF_TOKEN_NAME);
  },

  checkToken,
};

export default authService;
